import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { Timeline } from 'primereact/timeline';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Steps } from 'primereact/steps';
import { useTranslation } from 'react-i18next';
import CarteV2Service from '../../../../services/CarteV2Service';

/**
 * BatchManagement - Gestion unifiée des lots et cartes
 * Combine: Batch Management, Queue Production, Impression, Suivi Étapes
 */
const BatchManagement = () => {
    const { t } = useTranslation();
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [batchCards, setBatchCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);

    // Dialogs
    const [showBatchDetailsDialog, setShowBatchDetailsDialog] = useState(false);
    const [showCardDetailsDialog, setShowCardDetailsDialog] = useState(false);
    const [showBatchHistoryDialog, setShowBatchHistoryDialog] = useState(false);
    const [showCardHistoryDialog, setShowCardHistoryDialog] = useState(false);
    const [showRemoveCardDialog, setShowRemoveCardDialog] = useState(false);
    const [showChangeBatchStatusDialog, setShowChangeBatchStatusDialog] = useState(false);
    const [showChangeCardStatusDialog, setShowChangeCardStatusDialog] = useState(false);

    // Form states
    const [removeReason, setRemoveReason] = useState('');
    const [newBatchStatus, setNewBatchStatus] = useState('');
    const [newCardStatus, setNewCardStatus] = useState('');
    const [statusChangeReason, setStatusChangeReason] = useState('');
    const [batchHistory, setBatchHistory] = useState([]);
    const [cardHistory, setCardHistory] = useState([]);

    const toast = React.useRef(null);

    const batchStatuses = [
        { label: t('batchManagement.batchStatuses.PENDING'), value: 'PENDING', severity: 'warning' },
        { label: t('batchManagement.batchStatuses.PROCESSING'), value: 'PROCESSING', severity: 'info' },
        { label: t('batchManagement.batchStatuses.PRINTING'), value: 'PRINTING', severity: 'info' },
        { label: t('batchManagement.batchStatuses.QUALITY_CHECK'), value: 'QUALITY_CHECK', severity: 'warning' },
        { label: t('batchManagement.batchStatuses.COMPLETED'), value: 'COMPLETED', severity: 'success' },
        { label: t('batchManagement.batchStatuses.FAILED'), value: 'FAILED', severity: 'danger' },
        { label: t('batchManagement.batchStatuses.CANCELLED'), value: 'CANCELLED', severity: 'secondary' }
    ];

    const cardStatuses = [
        { label: t('batchManagement.cardStatuses.Ordered'), value: 'Ordered', severity: 'info' },
        { label: t('batchManagement.cardStatuses.PendingPayment'), value: 'PendingPayment', severity: 'warning' },
        { label: t('batchManagement.cardStatuses.PaymentConfirmed'), value: 'PaymentConfirmed', severity: 'success' },
        { label: t('batchManagement.cardStatuses.DataValidation'), value: 'DataValidation', severity: 'info' },
        { label: t('batchManagement.cardStatuses.PhotoProcessing'), value: 'PhotoProcessing', severity: 'info' },
        { label: t('batchManagement.cardStatuses.TemplateApplication'), value: 'TemplateApplication', severity: 'info' },
        { label: t('batchManagement.cardStatuses.InProduction'), value: 'InProduction', severity: 'warning' },
        { label: t('batchManagement.cardStatuses.QualityControl'), value: 'QualityControl', severity: 'warning' },
        { label: t('batchManagement.cardStatuses.Printing'), value: 'Printing', severity: 'info' },
        { label: t('batchManagement.cardStatuses.Printed'), value: 'Printed', severity: 'success' },
        { label: t('batchManagement.cardStatuses.Packaging'), value: 'Packaging', severity: 'info' },
        { label: t('batchManagement.cardStatuses.ReadyForDelivery'), value: 'ReadyForDelivery', severity: 'success' },
        { label: t('batchManagement.cardStatuses.Shipped'), value: 'Shipped', severity: 'info' },
        { label: t('batchManagement.cardStatuses.Delivered'), value: 'Delivered', severity: 'success' },
        { label: t('batchManagement.cardStatuses.Activated'), value: 'Activated', severity: 'success' },
        { label: t('batchManagement.cardStatuses.Suspended'), value: 'Suspended', severity: 'warning' },
        { label: t('batchManagement.cardStatuses.Expired'), value: 'Expired', severity: 'danger' },
        { label: t('batchManagement.cardStatuses.Defective'), value: 'Defective', severity: 'danger' },
        { label: t('batchManagement.cardStatuses.Lost'), value: 'Lost', severity: 'danger' },
        { label: t('batchManagement.cardStatuses.Replaced'), value: 'Replaced', severity: 'info' },
        { label: t('batchManagement.cardStatuses.Cancelled'), value: 'Cancelled', severity: 'secondary' },
        { label: t('batchManagement.cardStatuses.Completed'), value: 'Completed', severity: 'success' }
    ];

    const productionSteps = [
        { label: t('batchManagement.productionSteps.DataValidation'), value: 'DataValidation' },
        { label: t('batchManagement.productionSteps.PhotoProcessing'), value: 'PhotoProcessing' },
        { label: t('batchManagement.productionSteps.TemplateApplication'), value: 'TemplateApplication' },
        { label: t('batchManagement.productionSteps.InProduction'), value: 'InProduction' },
        { label: t('batchManagement.productionSteps.QualityControl'), value: 'QualityControl' },
        { label: t('batchManagement.productionSteps.Printing'), value: 'Printing' },
        { label: t('batchManagement.productionSteps.Packaging'), value: 'Packaging' },
        { label: t('batchManagement.productionSteps.ReadyForDelivery'), value: 'ReadyForDelivery' }
    ];

    // Batch statuses that cannot be modified
    const immutableBatchStatuses = ['COMPLETED', 'CANCELLED'];

    useEffect(() => {
        loadBatches();
        const interval = setInterval(loadBatches, 300000); // Refresh every 5 minutes
        return () => clearInterval(interval);
    }, [statusFilter]);

    const loadBatches = async () => {
        try {
            setLoading(true);
            const filters = {
                page: 1,
                pageSize: 100
            };
            if (statusFilter) {
                filters.status = statusFilter;
            }
            const response = await CarteV2Service.listBatches(filters);
            setBatches(response.data || response || []);
        } catch (error) {
            console.error('Error loading batches:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.batchLoadError'));
        } finally {
            setLoading(false);
        }
    };

    const loadBatchCards = async (batchId) => {
        try {
            const response = await CarteV2Service.getBatchCards(batchId, {
                page: 1,
                pageSize: 200
            });
            setBatchCards(response.data || response || []);
        } catch (error) {
            console.error('Error loading batch cards:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.cardsLoadError'));
        }
    };

    const loadBatchHistory = async (batchId) => {
        try {
            // Simulate history loading - replace with actual API call when available
            const mockHistory = [
                {
                    status: t('batchManagement.messages.batchCreated'),
                    date: new Date(),
                    notes: t('batchManagement.messages.batchCreated'),
                    user: 'Admin',
                    color: '#607D8B',
                    icon: 'pi pi-plus'
                }
            ];
            setBatchHistory(mockHistory);
        } catch (error) {
            console.error('Error loading batch history:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.batchHistoryLoadError'));
        }
    };

    const loadCardHistory = async (cardId) => {
        try {
            const history = await CarteV2Service.getCardHistory(cardId, {
                page: 1,
                pageSize: 50
            });

            // Transform history to timeline format
            const timelineData = (history.data || history || []).map(item => ({
                status: item.status || item.Status || 'Statut inconnu',
                date: item.date || item.Date || item.timestamp || new Date(),
                notes: item.notes || item.Notes || item.description || '',
                user: item.user || item.User || item.updatedBy || 'Système',
                color: getStatusColor(item.status || item.Status),
                icon: getStatusIcon(item.status || item.Status)
            }));

            setCardHistory(timelineData);
        } catch (error) {
            console.error('Error loading card history:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.cardHistoryLoadError'));
        }
    };

    const getStatusColor = (status) => {
        const colorMap = {
            'DataValidation': '#2196F3',
            'PreProduction': '#FF9800',
            'Printing': '#2196F3',
            'Encoding': '#2196F3',
            'QualityCheck': '#FF9800',
            'Finishing': '#4CAF50',
            'Completed': '#4CAF50',
            'Failed': '#F44336'
        };
        return colorMap[status] || '#607D8B';
    };

    const getStatusIcon = (status) => {
        const iconMap = {
            'DataValidation': 'pi pi-check-circle',
            'PreProduction': 'pi pi-cog',
            'Printing': 'pi pi-print',
            'Encoding': 'pi pi-code',
            'QualityCheck': 'pi pi-shield',
            'Finishing': 'pi pi-check-square',
            'Completed': 'pi pi-check',
            'Failed': 'pi pi-times-circle'
        };
        return iconMap[status] || 'pi pi-circle';
    };

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 5000 });
    };

    const downloadBatchPdf = async (batchId) => {
        try {
            const pdfBlob = await CarteV2Service.batchPrintCardsPdf({ batchId });

            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `batch_${batchId}_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('success', t('batchManagement.toasts.success'), t('batchManagement.toasts.pdfDownloadSuccess'));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.pdfDownloadError'));
        }
    };

    const removeCardFromBatch = async () => {
        if (!selectedCard || !selectedBatch || !removeReason.trim()) {
            showToast('warn', t('batchManagement.toasts.warning'), t('batchManagement.toasts.provideReason'));
            return;
        }

        try {
            await CarteV2Service.removeCardFromBatch(selectedBatch.id, selectedCard.id, {
                removedBy: localStorage.getItem('userEmail') || 'admin@company.com',
                reason: removeReason
            });

            showToast('success', t('batchManagement.toasts.success'), t('batchManagement.toasts.cardRemovedSuccess'));
            setShowRemoveCardDialog(false);
            setRemoveReason('');
            loadBatchCards(selectedBatch.id);
            loadBatches();
        } catch (error) {
            console.error('Error removing card:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.cardRemoveError'));
        }
    };

    const changeBatchStatus = async () => {
        if (!selectedBatch || !newBatchStatus) {
            showToast('warn', t('batchManagement.toasts.warning'), t('batchManagement.toasts.selectStatus'));
            return;
        }

        // Check if batch is immutable
        if (immutableBatchStatuses.includes(selectedBatch.status)) {
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.batchNotEditable'));
            return;
        }

        try {
            // API call to update batch status - implement when backend is ready
            showToast('success', t('batchManagement.toasts.success'), t('batchManagement.toasts.batchStatusUpdated'));
            setShowChangeBatchStatusDialog(false);
            setNewBatchStatus('');
            setStatusChangeReason('');
            loadBatches();
        } catch (error) {
            console.error('Error changing batch status:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.batchStatusUpdateError'));
        }
    };

    const changeCardStatus = async () => {
        if (!selectedCard || !newCardStatus) {
            showToast('warn', t('batchManagement.toasts.warning'), t('batchManagement.toasts.selectStatus'));
            return;
        }

        try {
            await CarteV2Service.updateCardStatus(selectedCard.id, {
                status: newCardStatus,
                reason: statusChangeReason,
                updatedBy: localStorage.getItem('userEmail') || 'admin@company.com'
            });

            showToast('success', t('batchManagement.toasts.success'), t('batchManagement.toasts.cardStatusUpdated'));
            setShowChangeCardStatusDialog(false);
            setNewCardStatus('');
            setStatusChangeReason('');
            if (selectedBatch) {
                loadBatchCards(selectedBatch.id);
            }
        } catch (error) {
            console.error('Error changing card status:', error);
            showToast('error', t('batchManagement.toasts.error'), t('batchManagement.toasts.cardStatusUpdateError'));
        }
    };

    const confirmRemoveCard = (card) => {
        setSelectedCard(card);
        setShowRemoveCardDialog(true);
    };

    const batchStatusBodyTemplate = (rowData) => {
        const statusConfig = batchStatuses.find(s => s.value === rowData.status) ||
                            { severity: 'secondary', label: rowData.status };
        return <Tag value={statusConfig.label} severity={statusConfig.severity} />;
    };

    const cardStatusBodyTemplate = (rowData) => {
        const statusConfig = cardStatuses.find(s => s.value === rowData.status) ||
                            { severity: 'secondary', label: rowData.status };
        return <Tag value={statusConfig.label} severity={statusConfig.severity} />;
    };

    const progressBodyTemplate = (rowData) => {
        const total = rowData.totalCards || 0;
        const completed = rowData.completedCards || 0;
        const progress = total > 0 ? (completed / total) * 100 : 0;

        return (
            <div>
                <div className="flex justify-content-between mb-1">
                    <small>{completed} / {total}</small>
                    <small>{progress.toFixed(0)}%</small>
                </div>
                <ProgressBar value={progress} showValue={false} style={{ height: '8px' }} />
            </div>
        );
    };

    const batchActionBodyTemplate = (rowData) => {
        const isImmutable = immutableBatchStatuses.includes(rowData.status);

        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-info p-button-sm"
                    tooltip={t('batchManagement.tooltips.seeDetails')}
                    onClick={() => {
                        setSelectedBatch(rowData);
                        loadBatchCards(rowData.id);
                        setShowBatchDetailsDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-download"
                    className="p-button-rounded p-button-success p-button-sm"
                    tooltip={t('batchManagement.tooltips.downloadPdf')}
                    onClick={() => downloadBatchPdf(rowData.id)}
                />
                <Button
                    icon="pi pi-history"
                    className="p-button-rounded p-button-help p-button-sm"
                    tooltip={t('batchManagement.tooltips.history')}
                    onClick={() => {
                        setSelectedBatch(rowData);
                        loadBatchHistory(rowData.id);
                        setShowBatchHistoryDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-warning p-button-sm"
                    tooltip={isImmutable ? t('batchManagement.tooltips.batchNotEditable') : t('batchManagement.tooltips.changeStatus')}
                    disabled={isImmutable}
                    onClick={() => {
                        setSelectedBatch(rowData);
                        setShowChangeBatchStatusDialog(true);
                    }}
                />
            </div>
        );
    };

    const cardActionBodyTemplate = (rowData) => {
        const batchIsImmutable = selectedBatch && immutableBatchStatuses.includes(selectedBatch.status);

        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-info p-button-sm"
                    tooltip={t('batchManagement.tooltips.seeDetails')}
                    onClick={() => {
                        setSelectedCard(rowData);
                        setShowCardDetailsDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-history"
                    className="p-button-rounded p-button-help p-button-sm"
                    tooltip={t('batchManagement.tooltips.history')}
                    onClick={() => {
                        setSelectedCard(rowData);
                        loadCardHistory(rowData.id);
                        setShowCardHistoryDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-warning p-button-sm"
                    tooltip={t('batchManagement.tooltips.changeStatus')}
                    onClick={() => {
                        setSelectedCard(rowData);
                        setShowChangeCardStatusDialog(true);
                    }}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-sm"
                    tooltip={batchIsImmutable ? t('batchManagement.tooltips.batchNotEditable') : t('batchManagement.tooltips.removeFromBatch')}
                    disabled={batchIsImmutable}
                    onClick={() => confirmRemoveCard(rowData)}
                />
            </div>
        );
    };

    const dateBodyTemplate = (rowData) => {
        const date = rowData.productionDate || rowData.createdDate || new Date();
        return new Date(date).toLocaleString('fr-FR');
    };

    const cardStepsBodyTemplate = (rowData) => {
        const currentIndex = productionSteps.findIndex(s => s.value === rowData.status);
        const activeIndex = currentIndex >= 0 ? currentIndex : 0;

        return (
            <div className="flex align-items-center">
                <Steps
                    model={productionSteps}
                    activeIndex={activeIndex}
                    readOnly
                    style={{ width: '100%', fontSize: '0.75rem' }}
                />
            </div>
        );
    };

    const filteredBatches = batches.filter(batch =>
        !searchTerm ||
        batch.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.countryCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const leftToolbarTemplate = () => {
        return (
            <div className="flex gap-2 align-items-center">
                <Button
                    label={t('batchManagement.buttons.refresh')}
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={loadBatches}
                    loading={loading}
                />
                <Badge value={`${filteredBatches.length} ${t('batchManagement.counters.batches')}`} severity="info" size="large" />
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        placeholder={t('batchManagement.filters.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </span>
                <Dropdown
                    value={statusFilter}
                    options={[{ label: t('batchManagement.filters.all'), value: null }, ...batchStatuses]}
                    onChange={(e) => setStatusFilter(e.value)}
                    placeholder={t('batchManagement.filters.filterByStatus')}
                    showClear
                />
            </div>
        );
    };

    const timelineMarker = (item) => {
        return (
            <span
                className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1"
                style={{ backgroundColor: item.color }}
            >
                <i className={item.icon}></i>
            </span>
        );
    };

    const timelineContent = (item) => {
        return (
            <Card title={item.status} subTitle={new Date(item.date).toLocaleString('fr-FR')}>
                <p>{item.notes || t('batchManagement.messages.noNote')}</p>
                {item.user && <small className="text-muted">{t('batchManagement.labels.performedBy')}: {item.user}</small>}
            </Card>
        );
    };

    return (
        <div className="batch-management-unified">
            <Toast ref={toast} />
            <ConfirmDialog />

            <Card title={t('batchManagement.title')} className="mb-4">
                <div className="mb-3">
                    <h5 className="m-0">{t('batchManagement.subtitle')}</h5>
                    <small className="text-muted">
                        {t('batchManagement.description')}
                    </small>
                </div>

                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="mb-3" />

                <DataTable
                    value={filteredBatches}
                    loading={loading}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    emptyMessage={t('batchManagement.messages.noBatches')}
                    selection={selectedBatch}
                    onSelectionChange={(e) => setSelectedBatch(e.value)}
                    dataKey="id"
                    sortField="productionDate"
                    sortOrder={-1}
                >
                    <Column selectionMode="single" headerStyle={{ width: '3em' }} />
                    <Column
                        field="batchNumber"
                        header={t('batchManagement.labels.batchNumber')}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        header={t('batchManagement.labels.productionDate')}
                        body={dateBodyTemplate}
                        sortable
                        style={{ minWidth: '160px' }}
                    />
                    <Column
                        field="totalCards"
                        header={t('batchManagement.labels.totalCards')}
                        sortable
                        style={{ width: '120px' }}
                    />
                    <Column
                        header={t('batchManagement.labels.progress')}
                        body={progressBodyTemplate}
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="status"
                        header={t('batchManagement.labels.status')}
                        body={batchStatusBodyTemplate}
                        sortable
                        style={{ width: '130px' }}
                    />
                    <Column
                        header={t('batchManagement.labels.actions')}
                        body={batchActionBodyTemplate}
                        style={{ width: '220px' }}
                    />
                </DataTable>
            </Card>

            {/* Dialog: Détails du Lot avec Cartes */}
            <Dialog
                header={`${t('batchManagement.dialogs.batchDetails')} ${selectedBatch?.batchNumber}`}
                visible={showBatchDetailsDialog}
                style={{ width: '90vw' }}
                onHide={() => {
                    setShowBatchDetailsDialog(false);
                    setSelectedBatch(null);
                    setBatchCards([]);
                }}
                maximizable
            >
                {selectedBatch && (
                    <div>
                        {/* Batch Info */}
                        <div className="grid mb-4">
                            <div className="col-3">
                                <strong>{t('batchManagement.labels.batchNumber')}:</strong> {selectedBatch.batchNumber}
                            </div>
                            <div className="col-3">
                                <strong>{t('batchManagement.labels.totalCards')}:</strong> {selectedBatch.totalCards}
                            </div>
                            <div className="col-3">
                                <strong>{t('batchManagement.labels.status')}:</strong> {batchStatusBodyTemplate(selectedBatch)}
                            </div>
                            <div className="col-6">
                                <strong>{t('batchManagement.labels.productionDate')}:</strong> {dateBodyTemplate(selectedBatch)}
                            </div>
                            <div className="col-6">
                                <strong>{t('batchManagement.labels.operator')}:</strong> {selectedBatch.printerOperator || selectedBatch.operatorName || 'N/A'}
                            </div>
                            {selectedBatch.notes && (
                                <div className="col-12">
                                    <strong>{t('batchManagement.labels.notes')}:</strong> {selectedBatch.notes}
                                </div>
                            )}
                        </div>

                        {/* Cards in Batch */}
                        <h5>{t('batchManagement.messages.cardsInBatch')} ({batchCards.length})</h5>
                        <DataTable
                            value={batchCards}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            emptyMessage={t('batchManagement.messages.noCards')}
                            dataKey="id"
                        >
                            <Column field="cardNumber" header={t('batchManagement.labels.cardNumber')} sortable style={{ minWidth: '120px' }} />
                            <Column field="beneficiaryName" header={t('batchManagement.labels.beneficiary')} sortable style={{ minWidth: '180px' }} />
                            <Column
                                field="status"
                                header={t('batchManagement.labels.status')}
                                body={cardStatusBodyTemplate}
                                sortable
                                style={{ width: '130px' }}
                            />
                            <Column
                                header={t('batchManagement.labels.progress')}
                                body={cardStepsBodyTemplate}
                                style={{ minWidth: '400px' }}
                            />
                            <Column
                                header={t('batchManagement.labels.actions')}
                                body={cardActionBodyTemplate}
                                style={{ width: '200px' }}
                            />
                        </DataTable>
                    </div>
                )}
            </Dialog>

            {/* Dialog: Détails de la Carte */}
            <Dialog
                header={`${t('batchManagement.dialogs.cardDetails')} ${selectedCard?.cardNumber}`}
                visible={showCardDetailsDialog}
                style={{ width: '50vw' }}
                onHide={() => {
                    setShowCardDetailsDialog(false);
                    setSelectedCard(null);
                }}
            >
                {selectedCard && (
                    <div className="grid">
                        <div className="col-6">
                            <strong>{t('batchManagement.labels.cardNumber')}:</strong> {selectedCard.cardNumber}
                        </div>
                        <div className="col-6">
                            <strong>{t('batchManagement.labels.beneficiary')}:</strong> {selectedCard.beneficiaryName}
                        </div>
                        <div className="col-6">
                            <strong>{t('batchManagement.labels.status')}:</strong> {cardStatusBodyTemplate(selectedCard)}
                        </div>
                        <div className="col-6">
                            <strong>{t('batchManagement.labels.date')}:</strong> {dateBodyTemplate(selectedCard)}
                        </div>
                        {selectedCard.serialNumber && (
                            <div className="col-12">
                                <strong>{t('batchManagement.labels.serialNumber')}:</strong> {selectedCard.serialNumber}
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Dialog: Historique du Lot */}
            <Dialog
                header={`${t('batchManagement.dialogs.batchHistory')} ${selectedBatch?.batchNumber}`}
                visible={showBatchHistoryDialog}
                style={{ width: '60vw' }}
                onHide={() => {
                    setShowBatchHistoryDialog(false);
                    setSelectedBatch(null);
                    setBatchHistory([]);
                }}
            >
                {batchHistory.length > 0 ? (
                    <Timeline
                        value={batchHistory}
                        content={timelineContent}
                        marker={timelineMarker}
                        className="customized-timeline"
                    />
                ) : (
                    <p className="text-center text-muted">{t('batchManagement.messages.noHistory')}</p>
                )}
            </Dialog>

            {/* Dialog: Historique de la Carte */}
            <Dialog
                header={`${t('batchManagement.dialogs.cardHistory')} ${selectedCard?.cardNumber}`}
                visible={showCardHistoryDialog}
                style={{ width: '60vw' }}
                onHide={() => {
                    setShowCardHistoryDialog(false);
                    setSelectedCard(null);
                    setCardHistory([]);
                }}
            >
                {cardHistory.length > 0 ? (
                    <Timeline
                        value={cardHistory}
                        content={timelineContent}
                        marker={timelineMarker}
                        className="customized-timeline"
                    />
                ) : (
                    <p className="text-center text-muted">{t('batchManagement.messages.loadingHistory')}</p>
                )}
            </Dialog>

            {/* Dialog: Retirer Carte du Lot */}
            <Dialog
                header={t('batchManagement.dialogs.removeCard')}
                visible={showRemoveCardDialog}
                style={{ width: '40vw' }}
                onHide={() => {
                    setShowRemoveCardDialog(false);
                    setSelectedCard(null);
                    setRemoveReason('');
                }}
            >
                <div className="mb-3">
                    <p>{t('batchManagement.messages.confirmRemoveCard')}</p>
                    <p><strong>{t('batchManagement.labels.cardNumber')}:</strong> {selectedCard?.cardNumber}</p>
                    <p className="text-red-500"><strong>{t('batchManagement.messages.removeWarning')}</strong></p>
                </div>
                <div className="field">
                    <label htmlFor="removeReason">{t('batchManagement.labels.reasonRequired')}</label>
                    <InputTextarea
                        id="removeReason"
                        value={removeReason}
                        onChange={(e) => setRemoveReason(e.target.value)}
                        rows={3}
                        placeholder={t('batchManagement.messages.explainReason')}
                        className="w-full"
                    />
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button
                        label={t('batchManagement.buttons.cancel')}
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={() => {
                            setShowRemoveCardDialog(false);
                            setRemoveReason('');
                        }}
                    />
                    <Button
                        label={t('batchManagement.buttons.remove')}
                        icon="pi pi-trash"
                        className="p-button-danger"
                        onClick={removeCardFromBatch}
                        disabled={!removeReason.trim()}
                    />
                </div>
            </Dialog>

            {/* Dialog: Changer Statut du Lot */}
            <Dialog
                header={`${t('batchManagement.dialogs.changeBatchStatus')} ${selectedBatch?.batchNumber}`}
                visible={showChangeBatchStatusDialog}
                style={{ width: '40vw' }}
                onHide={() => {
                    setShowChangeBatchStatusDialog(false);
                    setNewBatchStatus('');
                    setStatusChangeReason('');
                }}
            >
                <div className="field mb-3">
                    <label htmlFor="newBatchStatus">{t('batchManagement.labels.newStatus')} *</label>
                    <Dropdown
                        id="newBatchStatus"
                        value={newBatchStatus}
                        options={batchStatuses}
                        onChange={(e) => setNewBatchStatus(e.value)}
                        placeholder={t('batchManagement.messages.selectStatus')}
                        className="w-full"
                    />
                </div>
                <div className="field">
                    <label htmlFor="batchStatusReason">{t('batchManagement.labels.reasonOptional')}</label>
                    <InputTextarea
                        id="batchStatusReason"
                        value={statusChangeReason}
                        onChange={(e) => setStatusChangeReason(e.target.value)}
                        rows={3}
                        placeholder={t('batchManagement.messages.explainStatusChange')}
                        className="w-full"
                    />
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button
                        label={t('batchManagement.buttons.cancel')}
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={() => {
                            setShowChangeBatchStatusDialog(false);
                            setNewBatchStatus('');
                            setStatusChangeReason('');
                        }}
                    />
                    <Button
                        label={t('batchManagement.buttons.update')}
                        icon="pi pi-check"
                        className="p-button-success"
                        onClick={changeBatchStatus}
                        disabled={!newBatchStatus}
                    />
                </div>
            </Dialog>

            {/* Dialog: Changer Statut de la Carte */}
            <Dialog
                header={`${t('batchManagement.dialogs.changeCardStatus')} ${selectedCard?.cardNumber}`}
                visible={showChangeCardStatusDialog}
                style={{ width: '40vw' }}
                onHide={() => {
                    setShowChangeCardStatusDialog(false);
                    setNewCardStatus('');
                    setStatusChangeReason('');
                }}
            >
                <div className="field mb-3">
                    <label htmlFor="newCardStatus">{t('batchManagement.labels.newStatus')} *</label>
                    <Dropdown
                        id="newCardStatus"
                        value={newCardStatus}
                        options={cardStatuses}
                        onChange={(e) => setNewCardStatus(e.value)}
                        placeholder={t('batchManagement.messages.selectStatus')}
                        className="w-full"
                    />
                </div>
                <div className="field">
                    <label htmlFor="cardStatusReason">{t('batchManagement.labels.reasonOptional')}</label>
                    <InputTextarea
                        id="cardStatusReason"
                        value={statusChangeReason}
                        onChange={(e) => setStatusChangeReason(e.target.value)}
                        rows={3}
                        placeholder={t('batchManagement.messages.explainStatusChange')}
                        className="w-full"
                    />
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button
                        label={t('batchManagement.buttons.cancel')}
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={() => {
                            setShowChangeCardStatusDialog(false);
                            setNewCardStatus('');
                            setStatusChangeReason('');
                        }}
                    />
                    <Button
                        label={t('batchManagement.buttons.update')}
                        icon="pi pi-check"
                        className="p-button-success"
                        onClick={changeCardStatus}
                        disabled={!newCardStatus}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default BatchManagement;
